import type { FreeEmailAuditReport } from '@/lib/free-tools/email-audit/contracts'
import {
  FREE_EMAIL_AUDIT_MAX_HTML_BYTES,
  isFreeEmailAuditHtmlTooLarge,
  utf8ByteLength,
} from '@/lib/free-tools/email-audit/contracts'
import {
  EMAIL_AUDIT_FORM,
  type EmailAuditTabId,
} from '@/lib/site/email-audit-content'

/**
 * The free audit island's whole state, as a pure reducer: every change is an
 * event (a keystroke, a dropped file, a response), so the island needs no
 * effects and every transition is testable here.
 */

export type EmailAuditToolStatus =
  | { kind: 'editing' }
  | { kind: 'running' }
  | { kind: 'done'; report: FreeEmailAuditReport; html: string }
  | { kind: 'error'; message: string }

export type EmailAuditToolState = {
  tab: EmailAuditTabId
  html: string
  subject: string
  previewText: string
  /** The uploaded file, while the HTML came from one. */
  file: { name: string; bytes: number } | null
  /** A problem with what was entered, shown under the field. */
  inputError: string | null
  /**
   * The file read or sample download in flight. An edit, another load or a
   * new audit replaces it, and a completion that is no longer pending is
   * dropped, so a slow load never overwrites newer work.
   */
  pendingLoad: number | null
  status: EmailAuditToolStatus
}

export type EmailAuditToolAction =
  | { type: 'tab'; tab: EmailAuditTabId }
  | { type: 'html'; html: string }
  | { type: 'subject'; subject: string }
  | { type: 'previewText'; previewText: string }
  | { type: 'loadStarted'; load: number }
  | { type: 'file'; load: number; name: string; html: string }
  | {
      type: 'sample'
      load: number
      html: string
      subject: string
      previewText: string
    }
  | { type: 'loadFailed'; load: number; message: string }
  | { type: 'inputError'; message: string }
  | { type: 'started' }
  | { type: 'succeeded'; report: FreeEmailAuditReport; html: string }
  | { type: 'failed'; message: string }
  | { type: 'reset' }

export const INITIAL_EMAIL_AUDIT_TOOL_STATE: EmailAuditToolState = {
  tab: 'paste',
  html: '',
  subject: '',
  previewText: '',
  file: null,
  inputError: null,
  pendingLoad: null,
  status: { kind: 'editing' },
}

let lastLoad = 0

/** A fresh id for one file read or sample download. */
export function nextEmailAuditLoad(): number {
  lastLoad += 1
  return lastLoad
}

function editing(state: EmailAuditToolState): EmailAuditToolStatus {
  // Editing after a failure clears the failure; a running audit keeps going.
  return state.status.kind === 'running' ? state.status : { kind: 'editing' }
}

export function emailAuditToolReducer(
  state: EmailAuditToolState,
  action: EmailAuditToolAction
): EmailAuditToolState {
  switch (action.type) {
    case 'tab':
      return { ...state, tab: action.tab, inputError: null }
    case 'html':
      return {
        ...state,
        html: action.html,
        file: null,
        inputError: null,
        pendingLoad: null,
        status: editing(state),
      }
    case 'subject':
      return { ...state, subject: action.subject, pendingLoad: null }
    case 'previewText':
      return { ...state, previewText: action.previewText, pendingLoad: null }
    case 'loadStarted':
      return { ...state, pendingLoad: action.load }
    case 'file':
      if (action.load !== state.pendingLoad) {
        return state
      }
      return {
        ...state,
        html: action.html,
        file: { name: action.name, bytes: utf8ByteLength(action.html) },
        inputError: null,
        pendingLoad: null,
        status: editing(state),
      }
    case 'sample':
      if (action.load !== state.pendingLoad) {
        return state
      }
      return {
        ...state,
        tab: 'paste',
        html: action.html,
        subject: action.subject,
        previewText: action.previewText,
        file: null,
        inputError: null,
        pendingLoad: null,
        status: editing(state),
      }
    case 'loadFailed':
      if (action.load !== state.pendingLoad) {
        return state
      }
      return { ...state, inputError: action.message, pendingLoad: null }
    case 'inputError':
      return { ...state, inputError: action.message }
    case 'started':
      return {
        ...state,
        inputError: null,
        pendingLoad: null,
        status: { kind: 'running' },
      }
    case 'succeeded':
      return {
        ...state,
        status: { kind: 'done', report: action.report, html: action.html },
      }
    case 'failed':
      return { ...state, status: { kind: 'error', message: action.message } }
    case 'reset':
      return { ...INITIAL_EMAIL_AUDIT_TOOL_STATE, tab: state.tab }
    default:
      return state
  }
}

/** Why the entered HTML cannot be audited yet, or `null` when it can. */
export function emailAuditInputProblem(html: string): string | null {
  if (html.trim().length === 0) {
    return EMAIL_AUDIT_FORM.emptyHtml
  }
  if (isFreeEmailAuditHtmlTooLarge(html)) {
    return EMAIL_AUDIT_FORM.fileTooLarge
  }
  return null
}

const HTML_FILE_NAME = /\.html?$/i

/** Why a chosen file cannot be read as an email, or `null` when it can. */
export function emailAuditFileProblem(file: {
  name: string
  size: number
  type: string
}): string | null {
  const isHtml = HTML_FILE_NAME.test(file.name) || file.type === 'text/html'
  if (!isHtml) {
    return EMAIL_AUDIT_FORM.fileRejected
  }
  if (file.size > FREE_EMAIL_AUDIT_MAX_HTML_BYTES) {
    return EMAIL_AUDIT_FORM.fileTooLarge
  }
  return null
}
